import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import { simpleDateColumn } from "@/components/DataTable/Columns/simpleDateColumn";
import { simpleLinkColumn } from "@/components/DataTable/Columns/simpleLinkColumn";
import { simpleTextColumn } from "@/components/DataTable/Columns/simpleTextColumn";
import { DataTableViewOptions } from "@/components/DataTable/Table/DataTableViewOptions";
import type { ColumnDef } from "@tanstack/table-core";
import type { ListShadowItVisitHistoriesResponse } from "../-query/type";
import { appCategoryColumn } from "../../-components/app-category-column";
import { ShadowItUser } from "../../-components/shadow-it-user";
import { traceableAppColumn } from "../../-components/traceable-app-column";
import { isShadowIdVisit } from "../../-utils/isShadowId";
import { LoginIdCell } from "../-components/LoginIdCell";

type Row = ListShadowItVisitHistoriesResponse["items"][number];

export const ColumnKey = {
	visitedAt: "visitedAt",
	user: "user",
  loginId: "loginId",
	traceableApp: "traceableApp",
	category: "category",
	url: "url",
	detectionTool: "detectionTool",
	actions: "actions",
} as const;

export const SortableColumnKey = [ColumnKey.visitedAt] as const;

function usersColumn(): ColumnDef<Row> {
	return {
		accessorKey: ColumnKey.user,
		meta: { i18nKey: "shadowIt.user", thStyle: { minWidth: "160px" } },
		enableSorting: false,
		header: DataTableColumnHeader,
		cell: ({ getValue }) => {
			const user = getValue<Row["user"]>();
			return <ShadowItUser user={user} showDetail={true} />;
		},
	};
}

function loginIdColumn(): ColumnDef<Row> {
	return {
		accessorKey: ColumnKey.loginId,
		meta: {
			i18nKey: "shadowIt.usedLoginId",
			thStyle: { minWidth: "200px" },
		},
		enableSorting: false,
		header: DataTableColumnHeader,
		cell: ({ row }) => {
			const isShadowId = isShadowIdVisit(row.original.loginId, row.original.user, {
				violationType: row.original.violationType,
			});

			return <LoginIdCell loginId={row.original.loginId} isShadowId={isShadowId} />;
		},
	};
}

function actionsColumn(): ColumnDef<Row> {
  return {
    accessorKey: ColumnKey.actions,
    header: ({ table }) => (
      <div className="flex justify-end pr-2">
        <DataTableViewOptions table={table} />
      </div>
    ),
  };
}

export function useShadowItVisitHistoryColumns(): ColumnDef<Row>[] {
	return [
		simpleDateColumn(ColumnKey.visitedAt, "dateTimeWeekday", {
			i18nKey: "shadowIt.visitedAt",
			sortable: true,
		}),
		usersColumn(),
		loginIdColumn(),
		traceableAppColumn(ColumnKey.traceableApp, {
			i18nKey: "shadowIt.app",
			sortable: false,
			showShadowIdBadge: true,
		}),
		appCategoryColumn(ColumnKey.category, (row) => row.traceableApp.category, {
			i18nKey: "shadowIt.category",
			sortable: false,
		}),
		simpleLinkColumn(ColumnKey.url, {
			i18nKey: "shadowIt.url",
			showLeadingIcon: false,
			trailingIcon: "SquareArrowOutUpRightIcon",
		}),
		simpleTextColumn(ColumnKey.detectionTool, {
			i18nKey: "shadowIt.detectionTool",
		}),
		actionsColumn(),
	];
}
